import { useState } from "react";
import Button from "@/lib/Button";
import $api from "@/lib/queries/openapi";
import { useParams } from "react-router";
import Select from "react-select";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import SettingsSectionLayout from "@/lib/SettingsSectionLayout";

export default function Users() {
  const [invitationIdsResent, setInvitationIdsResent] = useState<string[]>([]);
  const [newInvitation, setNewInvitation] = useState({
    email: "",
    role: roleOptions[0],
  });
  const [showNewInvitation, setShowNewInvitation] = useState(false);
  const { teamSlug } = useParams();
  const path = "/api/teams/{team_slug}/invitations";
  const params = {
    params: {
      path: {
        team_slug: teamSlug!,
      },
    },
  };
  const { data: invitations } = $api.useQuery("get", path, params);
  const allInvitations = invitations?.items ?? [];
  const pendingInvitations = allInvitations.filter(
    ({ is_accepted }) => !is_accepted,
  );

  const queryClient = useQueryClient();
  const invalidateQueries = () =>
    queryClient.invalidateQueries({
      queryKey: ["get", path, params],
    });
  const { mutate: sendInvitation } = $api.useMutation("post", path, {
    onSuccess: invalidateQueries,
  });
  const { mutate: resendInvitation } = $api.useMutation(
    "post",
    `${path}/{id}/resend`,
    {
      onSuccess: invalidateQueries,
    },
  );
  const { mutate: cancelInvitation } = $api.useMutation(
    "delete",
    `${path}/{id}`,
    {
      onSuccess: invalidateQueries,
    },
  );

  const { data: teamMembersResponse } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/members",
    params,
  );
  const teamMembers = teamMembersResponse?.items ?? [];

  return (
    <SettingsSectionLayout title="Users">
      <div className="flex flex-col gap-8 items-start text-sm">
        <table className="w-[1000px]">
          <thead className="font-bold">
            <tr>
              <th scope="col" className="text-left py-2">
                Name
              </th>
              <th scope="col" className="text-left py-2">
                Email
              </th>
              <th scope="col" className="text-left">
                Role
              </th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map(({ id, name, email, role }) => (
              <tr key={id}>
                <td className="p-2">{name}</td>
                <td>{email}</td>
                <td>{role}</td>
              </tr>
            ))}
            <tr>
              <td className="font-bold pt-8">Pending Invitations</td>
            </tr>
            {pendingInvitations.map(({ id, email, role }) => (
              <tr key={id}>
                <td></td>
                <td>{email}</td>
                <td>{role}</td>
                <td className="flex gap-2 mt-2">
                  <Button
                    disabled={invitationIdsResent.includes(id)}
                    className="text-blue-600"
                    onClick={() =>
                      resendInvitation(
                        {
                          params: {
                            path: {
                              team_slug: teamSlug!,
                              id,
                            },
                          },
                        },
                        {
                          onSuccess: () => {
                            setInvitationIdsResent([
                              ...invitationIdsResent,
                              id,
                            ]);
                            toast.success("Invitation resent");
                          },
                          onError: (msg) => toast.error(JSON.stringify(msg)),
                        },
                      )
                    }
                  >
                    Resend Invitation
                  </Button>
                  <Button
                    className="text-blue-600"
                    onClick={() =>
                      cancelInvitation(
                        {
                          params: {
                            path: {
                              team_slug: teamSlug!,
                              id,
                            },
                          },
                        },
                        {
                          onSuccess: () =>
                            toast.success("Invitation cancelled"),
                          onError: (msg) => toast.error(JSON.stringify(msg)),
                        },
                      )
                    }
                  >
                    Cancel Invitation
                  </Button>
                </td>
              </tr>
            ))}
            <tr>
              <td className={`py-4 ${showNewInvitation ? "invisible" : ""}`}>
                <Button
                  className="border"
                  onClick={() => setShowNewInvitation(true)}
                >
                  New Invitation
                </Button>
              </td>
              <td className={`${showNewInvitation ? "" : "invisible"}`}>
                <input
                  placeholder="Email"
                  className="w-52 border rounded-sm p-2"
                  value={newInvitation.email}
                  onChange={(e) =>
                    setNewInvitation({
                      ...newInvitation,
                      email: e.target.value,
                    })
                  }
                />
              </td>
              <td className={`${showNewInvitation ? "" : "invisible"}`}>
                <Select
                  placeholder="Role"
                  onChange={(option) =>
                    setNewInvitation({
                      ...newInvitation,
                      role: option ?? roleOptions[0],
                    })
                  }
                  value={newInvitation.role}
                  options={[
                    { label: "Admin", value: "admin" },
                    { label: "Member", value: "member" },
                  ]}
                  className="text-sm w-28"
                />
              </td>
              <td
                className={`flex gap-2 items-end p-6 ${showNewInvitation ? "" : "invisible"}`}
              >
                <Button
                  disabled={!newInvitation.email}
                  primary
                  onClick={() =>
                    sendInvitation(
                      {
                        ...params,
                        body: {
                          email: newInvitation.email,
                          role: newInvitation.role.value,
                        },
                      },
                      {
                        onSuccess: () => {
                          setShowNewInvitation(false);
                          setNewInvitation({
                            email: "",
                            role: roleOptions[0],
                          });
                          toast.success("Invitation sent");
                        },
                        onError: (msg) => toast.error(JSON.stringify(msg)),
                      },
                    )
                  }
                >
                  Send Invitation
                </Button>
                <Button
                  onClick={() => setShowNewInvitation(false)}
                  className="border"
                >
                  Cancel
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </SettingsSectionLayout>
  );
}

const roleOptions = [
  { label: "Member", value: "member" },
  { label: "Admin", value: "admin" },
];
